import { useTranslation } from 'react-i18next';
// frontend/src/components/chat/AudioPlayer.jsx
// Reproductor premium de notas de voz con waveform interactivo.
// - Dibuja attachment.waveform si viene; si no, una aproximación estática.
// - Click/arrastre sobre la onda = scrubbing (saltar a una posición).
// - Reproduce audios de web (webm/opus) y de la app (m4a/aac) usando <audio>
//   con la URL autenticada por ?token= (el navegador no envía Authorization).
// - Manejo de errores de carga con botón de reintento, sin congelar la UI.
import React, { useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, IconButton, Typography, Tooltip } from '@mui/material';
import {
  PlayArrow as PlayIcon, Pause as PauseIcon, Refresh as RetryIcon,
} from '@mui/icons-material';
import { api } from '../../services/api';

const fmtDur = (s) => {
  if (s == null || Number.isNaN(s) || !Number.isFinite(s)) return '0:00';
  const m = Math.floor(s / 60);
  const ss = Math.floor(s % 60);
  return `${m}:${String(ss).padStart(2, '0')}`;
};

const BARS = 48;

// Normaliza el waveform de entrada a BARS picos 0..1; si no hay, genera una
// silueta pseudoaleatoria estable a partir del id (mejor que una línea plana).
const useBars = (attachment) => useMemo(() => {
  const wf = attachment.waveform;
  if (Array.isArray(wf) && wf.length) {
    if (wf.length === BARS) return wf.map((v) => Math.min(1, Math.max(0.04, v)));
    const out = [];
    for (let i = 0; i < BARS; i += 1) {
      const v = wf[Math.floor((i / BARS) * wf.length)] || 0;
      out.push(Math.min(1, Math.max(0.04, v)));
    }
    return out;
  }
  // Aproximación determinista basada en el id del adjunto.
  let seed = String(attachment.id || '0').split('').reduce((a, c) => a + c.charCodeAt(0), 0);
  const rnd = () => { seed = (seed * 9301 + 49297) % 233280; return seed / 233280; };
  return Array.from({ length: BARS }, () => 0.25 + rnd() * 0.6);
}, [attachment.id, attachment.waveform]);

const AudioPlayer = ({ attachment }) => {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const waveRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(attachment.duration_seconds || 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  const bars = useBars(attachment);
  const ratio = duration > 0 ? Math.min(1, progress / duration) : 0;

  // Carga perezosa de la fuente autenticada al primer play.
  const ensureSrc = () => {
    const el = audioRef.current;
    if (!el) return;
    if (!el.src) {
      el.src = api.chat.attachmentStreamUrl(attachment.id);
      setLoading(true);
    }
  };

  const toggle = async () => {
    const el = audioRef.current;
    if (!el) return;
    if (error) { // reintento manual
      setError(false);
      el.src = `${api.chat.attachmentStreamUrl(attachment.id)}${api.chat.attachmentStreamUrl(attachment.id).includes('?') ? '&' : '?'}r=${retryKey + 1}`;
      setRetryKey((k) => k + 1);
    }
    ensureSrc();
    if (playing) { el.pause(); return; }
    try { await el.play(); } catch { setError(true); setLoading(false); }
  };

  const onTime = () => {
    const el = audioRef.current;
    if (!el) return;
    setProgress(el.currentTime || 0);
    if (el.duration && Number.isFinite(el.duration)) setDuration(el.duration);
  };

  // Scrubbing: click o arrastre sobre la onda → salta a la posición.
  const seekToClientX = (clientX) => {
    const el = audioRef.current;
    const wave = waveRef.current;
    if (!el || !wave || !duration) return;
    const rect = wave.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    const t = pct * duration;
    ensureSrc();
    try { el.currentTime = t; } catch { /* noop */ }
    setProgress(t);
  };

  const onWaveDown = (e) => {
    seekToClientX(e.clientX);
    const move = (ev) => seekToClientX(ev.clientX);
    const up = () => { window.removeEventListener('mousemove', move); window.removeEventListener('mouseup', up); };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 230, maxWidth: 320, py: 0.25 }}>
      <audio
        ref={audioRef}
        preload="none"
        onLoadStart={() => setLoading(true)}
        onCanPlay={() => setLoading(false)}
        onLoadedMetadata={onTime}
        onTimeUpdate={onTime}
        onPlay={() => { setPlaying(true); setLoading(false); }}
        onPause={() => setPlaying(false)}
        onEnded={() => { setPlaying(false); setProgress(0); }}
        onError={() => { setError(true); setLoading(false); setPlaying(false); }}
      />
      <IconButton size="small" onClick={toggle} disabled={loading}
        sx={{ bgcolor: error ? 'grey.400' : 'primary.main', color: '#fff', '&:hover': { bgcolor: error ? 'grey.500' : 'primary.dark' }, width: 32, height: 32, flexShrink: 0 }}>
        {loading ? <CircularProgress size={16} sx={{ color: '#fff' }} />
          : error ? <RetryIcon fontSize="small" />
            : playing ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
      </IconButton>

      {/* Onda interactiva */}
      <Box
        ref={waveRef}
        onMouseDown={error ? undefined : onWaveDown}
        sx={{
          flex: 1, display: 'flex', alignItems: 'center', gap: '2px', height: 30,
          cursor: error ? 'default' : 'pointer', mx: 0.5, position: 'relative',
        }}
      >
        {bars.map((v, i) => {
          const filled = (i / BARS) <= ratio;
          return (
            <Box key={i} sx={{
              flex: 1, minWidth: 2, borderRadius: 2,
              height: `${Math.max(12, v * 100)}%`,
              bgcolor: filled ? 'primary.main' : 'primary.100',
              opacity: filled ? 1 : 0.55,
              transition: 'background-color 80ms linear',
            }} />
          );
        })}
      </Box>

      <Typography variant="caption" sx={{ color: error ? 'error.main' : 'text.secondary', minWidth: 36, textAlign: 'right', flexShrink: 0 }}>
        {error ? (
          <Tooltip title={t('hardcoded.components_chat_audioplayer.error_al_cargar_toca_para')}><span>Error</span></Tooltip>
        ) : fmtDur(playing || progress > 0 ? progress : duration)}
      </Typography>
    </Box>
  );
};

export default AudioPlayer;
